import React from 'react'
import { observer } from 'mobx-react'
import Button from 'components/Button'
import { Dropdown, DropdownTitle } from 'components/Dropdown'
import Icon from 'components/Icon'
import { IconButton } from 'components/IconButton'
import { formatToLongDateWithComma } from 'utils/date-util'
import styles from './ClientUserDetailsInformation.sass'
import { ClientUserDetailsViewModel } from '../ClientUserDetails/ClientUserDetailsViewModel'

const { isSupported, copy } = require('clipbrd')

interface ClientUserDetailsInformationProps {
  vm: ClientUserDetailsViewModel
}

export default observer(({ vm }: ClientUserDetailsInformationProps) => {
  const user = vm.member

  const copyTextToClipboard = (text: string) => {
    if (isSupported()) {
      copy(text)
    }
  }

  return (
    <>
      <UserInfoCard title="User information">
        <UserInfoLine title="Full name" value={user.displayName} />
        <UserInfoLine
          title="Email address"
          value={user.email}
          buttonText="Copy"
          buttonAction={() => copyTextToClipboard(user.email)}
        />
        <UserInfoLine
          title="Phone"
          value={user.phone}
          buttonText="Copy"
          buttonAction={() => copyTextToClipboard(user.phone)}
        />
        {user.lastSeen && (
          <UserInfoLine
            title="Last login"
            value={formatToLongDateWithComma(user.lastSeen)}
          />
        )}
        <UserInfoLine
          title="Last user update"
          value={formatToLongDateWithComma(user.dateModified)}
        />
        <UserInfoLine
          title="Date joined"
          value={formatToLongDateWithComma(user.dateCreated)}
        />
      </UserInfoCard>
    </>
  )
})

interface UserDetailsCardProps {
  title: string
  actions?: React.ReactNode
  children: React.ReactNode
}

const UserInfoCard = observer(
  ({ title, actions, children }: UserDetailsCardProps) => {
    return (
      <div className={styles.userInfoCard}>
        <div className={styles.userInfoTitle}>
          <span>{title}</span>
          {actions && (
            <Dropdown
              autoDismiss
              position="bottom-end"
              button={
                <IconButton dataTest={'user-details-info-actions'}>
                  <Icon name="more" color="var(--grey600)" />
                </IconButton>
              }
            >
              <DropdownTitle>
                <span>Actions</span>
              </DropdownTitle>
              {actions}
            </Dropdown>
          )}
        </div>
        {children}
      </div>
    )
  },
)

interface UserInfoLineProps {
  title: string
  value: any
  buttonText?: string
  buttonAction?: () => void
  'data-test'?: string
}

const UserInfoLine = observer(
  ({
    title,
    value,
    buttonText,
    buttonAction,
    'data-test': dataTest,
  }: UserInfoLineProps) => {
    const button = buttonText && buttonAction
    const normalizeTitle = title.toLowerCase().replace(/\s+/g, '-')

    return (
      <div className={styles.userInfoLine}>
        <span className={styles.infoLineTitle}>{title}</span>
        <div className={styles.infoLineSubtitle}>
          <span
            className={styles.infoLineValue}
            data-test={`user-info-line-${normalizeTitle}-value`}
          >
            {value}
          </span>
          {button && (
            <Button
              className={styles.infoLineButton}
              type="text"
              onClick={buttonAction}
              data-test={dataTest}
            >
              {buttonText}
            </Button>
          )}
        </div>
      </div>
    )
  },
)

export function parseCapacity(capacity?: number) {
  return capacity === 0 ? '∞' : capacity
}
